// src/pages/admin/tabs/CorporationTab.jsx
import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Field, ValidatedField, Actions, Btn, YesNoToggle } from '../ui';
import RichText from '../components/RichText';

export default function CorporationTab() {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [errors, setErrors] = React.useState({});

  const [form, setForm] = React.useState({
    // Основная информация
    title_ru: 'Корпорация АСТОРИУС',
    title_en: 'ASTORIUS Corporation',
    title_zh: '',
    intro_ru: '',
    intro_en: '',
    intro_zh: '',
    
    // О корпорации
    about_title_ru: 'О корпорации',
    about_title_en: 'About Corporation',
    about_title_zh: '',
    about_content_ru: '',
    about_content_en: '',
    about_content_zh: '',
    mission_title_ru: 'Наша миссия',
    mission_title_en: 'Our Mission',
    mission_title_zh: '',
    mission_content_ru: '',
    mission_content_en: '',
    mission_content_zh: '',
    quote_ru: '',
    quote_en: '',
    quote_zh: '',
    
    // Корпоративные ценности
    values_title_ru: 'Корпоративные ценности',
    values_title_en: 'Corporate Values',
    values_title_zh: '',
    values_list_ru: '',
    values_list_en: '',
    values_list_zh: '',
    
    // Направления деятельности
    directions_title_ru: 'Направления деятельности',
    directions_title_en: 'Business Directions',
    directions_title_zh: '',
    directions_content_ru: '',
    directions_content_en: '',
    directions_content_zh: '',
    
    // Корпоративная структура
    structure_title_ru: 'Корпоративная структура',
    structure_title_en: 'Corporate Structure',
    structure_title_zh: '',
    structure_content_ru: '',
    structure_content_en: '',
    structure_content_zh: '',
    structure_list_ru: '',
    structure_list_en: '',
    structure_list_zh: '',
    
    // Достижения
    achievements_title_ru: 'Достижения и признание',
    achievements_title_en: 'Achievements and Recognition',
    achievements_title_zh: '',
    achievements_content_ru: '',
    achievements_content_en: '',
    achievements_content_zh: '',
    achievements_highlight_ru: '',
    achievements_highlight_en: '',
    achievements_highlight_zh: '',
    achievements_list_ru: '',
    achievements_list_en: '',
    achievements_list_zh: '',
    
    // Мета-данные
    meta_title_ru: '',
    meta_title_en: '',
    meta_title_zh: '',
    meta_description_ru: '',
    meta_description_en: '',
    meta_description_zh: '',
    
    // Настройки
    is_published: true,
    is_active: true,
  });

  const patch = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Очистить ошибку при изменении поля
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Загрузка данных
  const loadData = React.useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const data = await AdminAPI.getCorporationContent();
      if (data) {
        setForm(prev => ({ ...prev, ...data }));
      }
    } catch (e) {
      console.error('Failed to load corporation content:', e);
      setMsg('Не удалось загрузить данные. Используются значения по умолчанию.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Валидация
  const validate = () => {
    const newErrors = {};
    
    if (!form.title_ru?.trim() && !form.title_en?.trim()) {
      newErrors.title_ru = 'Укажите заголовок (RU или EN)';
    }
    
    if (!form.intro_ru?.trim() && !form.intro_en?.trim()) {
      newErrors.intro_ru = 'Укажите вводный текст (RU или EN)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Сохранение
  const handleSave = async () => {
    if (!validate()) {
      setMsg('Исправьте ошибки в форме');
      return;
    }

    setSaving(true);
    setMsg('');
    try {
      await AdminAPI.updateCorporationContent(form);
      setMsg('Контент корпоративной страницы сохранен');
    } catch (e) {
      setMsg(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div>Загрузка...</div>
      </Card>
    );
  }

  return (
    <>
      <Actions style={{ marginBottom: 16 }}>
        <Btn $kind="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить изменения'}
        </Btn>
      </Actions>

      {msg && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ color: msg.includes('Ошибка') ? '#ef4444' : '#10b981' }}>
            {msg}
          </div>
        </Card>
      )}

      <Card>
        <h3>Основная информация</h3>
        
        <ValidatedField $error={errors.title_ru}>
          Заголовок (RU) *
          <input
            type="text"
            value={form.title_ru}
            onChange={(e) => patch('title_ru', e.target.value)}
            placeholder="Корпорация АСТОРИУС"
          />
          {errors.title_ru && <div className="error-message">{errors.title_ru}</div>}
        </ValidatedField>

        <Field>
          Заголовок (EN)
          <input
            type="text"
            value={form.title_en}
            onChange={(e) => patch('title_en', e.target.value)}
            placeholder="ASTORIUS Corporation"
          />
        </Field>

        <Field>
          Заголовок (ZH)
          <input
            type="text"
            value={form.title_zh}
            onChange={(e) => patch('title_zh', e.target.value)}
            placeholder="阿斯托里乌斯公司"
          />
        </Field>

        <ValidatedField $error={errors.intro_ru}>
          Вводный текст (RU) *
          <textarea
            value={form.intro_ru}
            onChange={(e) => patch('intro_ru', e.target.value)}
            placeholder="Краткое описание корпорации..."
            rows={3}
          />
          {errors.intro_ru && <div className="error-message">{errors.intro_ru}</div>}
        </ValidatedField>

        <Field>
          Вводный текст (EN)
          <textarea
            value={form.intro_en}
            onChange={(e) => patch('intro_en', e.target.value)}
            placeholder="Brief description of the corporation..."
            rows={3}
          />
        </Field>

        <Field>
          Вводный текст (ZH)
          <textarea
            value={form.intro_zh}
            onChange={(e) => patch('intro_zh', e.target.value)}
            placeholder="公司简介..."
            rows={3}
          />
        </Field>
      </Card>

      <Card>
        <h3>О корпорации</h3>
        
        <Field>
          Заголовок секции (RU)
          <input
            type="text"
            value={form.about_title_ru}
            onChange={(e) => patch('about_title_ru', e.target.value)}
            placeholder="О корпорации"
          />
        </Field>

        <Field>
          Заголовок секции (EN)
          <input
            type="text"
            value={form.about_title_en}
            onChange={(e) => patch('about_title_en', e.target.value)}
            placeholder="About Corporation"
          />
        </Field>

        <Field>
          Заголовок секции (ZH)
          <input
            type="text"
            value={form.about_title_zh}
            onChange={(e) => patch('about_title_zh', e.target.value)}
            placeholder="关于公司"
          />
        </Field>

        <Field>
          Основной текст (RU)
          <RichText
            value={form.about_content_ru}
            onChange={(value) => patch('about_content_ru', value)}
            placeholder="Описание корпорации..."
          />
        </Field>

        <Field>
          Основной текст (EN)
          <RichText
            value={form.about_content_en}
            onChange={(value) => patch('about_content_en', value)}
            placeholder="Corporation description..."
          />
        </Field>

        <Field>
          Основной текст (ZH)
          <RichText
            value={form.about_content_zh}
            onChange={(value) => patch('about_content_zh', value)}
            placeholder="公司描述..."
          />
        </Field>

        <Field>
          Заголовок миссии (RU)
          <input
            type="text"
            value={form.mission_title_ru}
            onChange={(e) => patch('mission_title_ru', e.target.value)}
            placeholder="Наша миссия"
          />
        </Field>

        <Field>
          Заголовок миссии (EN)
          <input
            type="text"
            value={form.mission_title_en}
            onChange={(e) => patch('mission_title_en', e.target.value)}
            placeholder="Our Mission"
          />
        </Field>

        <Field>
          Заголовок миссии (ZH)
          <input
            type="text"
            value={form.mission_title_zh}
            onChange={(e) => patch('mission_title_zh', e.target.value)}
            placeholder="我们的使命"
          />
        </Field>

        <Field>
          Текст миссии (RU)
          <textarea
            value={form.mission_content_ru}
            onChange={(e) => patch('mission_content_ru', e.target.value)}
            placeholder="Описание миссии корпорации..."
            rows={3}
          />
        </Field>

        <Field>
          Текст миссии (EN)
          <textarea
            value={form.mission_content_en}
            onChange={(e) => patch('mission_content_en', e.target.value)}
            placeholder="Corporation mission description..."
            rows={3}
          />
        </Field>

        <Field>
          Текст миссии (ZH)
          <textarea
            value={form.mission_content_zh}
            onChange={(e) => patch('mission_content_zh', e.target.value)}
            placeholder="公司使命描述..."
            rows={3}
          />
        </Field>

        <Field>
          Цитата/Девиз (RU)
          <textarea
            value={form.quote_ru}
            onChange={(e) => patch('quote_ru', e.target.value)}
            placeholder="Мы не просто продаем недвижимость — мы создаем будущее наших клиентов"
            rows={2}
          />
        </Field>

        <Field>
          Цитата/Девиз (EN)
          <textarea
            value={form.quote_en}
            onChange={(e) => patch('quote_en', e.target.value)}
            placeholder="We don't just sell real estate — we create our clients' future"
            rows={2}
          />
        </Field>

        <Field>
          Цитата/Девиз (ZH)
          <textarea
            value={form.quote_zh}
            onChange={(e) => patch('quote_zh', e.target.value)}
            placeholder="我们不仅仅销售房地产——我们创造客户的未来"
            rows={2}
          />
        </Field>
      </Card>

      <Card>
        <h3>Корпоративные ценности</h3>
        
        <Field>
          Заголовок (RU)
          <input
            type="text"
            value={form.values_title_ru}
            onChange={(e) => patch('values_title_ru', e.target.value)}
            placeholder="Корпоративные ценности"
          />
        </Field>

        <Field>
          Заголовок (EN)
          <input
            type="text"
            value={form.values_title_en}
            onChange={(e) => patch('values_title_en', e.target.value)}
            placeholder="Corporate Values"
          />
        </Field>

        <Field>
          Список ценностей (RU)
          <small>Каждая ценность с новой строки</small>
          <textarea
            value={form.values_list_ru}
            onChange={(e) => patch('values_list_ru', e.target.value)}
            placeholder="Безупречность в каждой детали&#10;Прозрачность всех процессов&#10;Инновационный подход к решению задач"
            rows={6}
          />
        </Field>

        <Field>
          Список ценностей (EN)
          <small>Каждая ценность с новой строки</small>
          <textarea
            value={form.values_list_en}
            onChange={(e) => patch('values_list_en', e.target.value)}
            placeholder="Excellence in every detail&#10;Transparency of all processes&#10;Innovative approach to problem solving"
            rows={6}
          />
        </Field>
      </Card>

      <Card>
        <h3>Направления деятельности</h3>
        
        <Field>
          Заголовок (RU)
          <input
            type="text"
            value={form.directions_title_ru}
            onChange={(e) => patch('directions_title_ru', e.target.value)}
            placeholder="Направления деятельности"
          />
        </Field>

        <Field>
          Заголовок (EN)
          <input
            type="text"
            value={form.directions_title_en}
            onChange={(e) => patch('directions_title_en', e.target.value)}
            placeholder="Business Directions"
          />
        </Field>

        <Field>
          Контент (RU)
          <RichText
            value={form.directions_content_ru}
            onChange={(value) => patch('directions_content_ru', value)}
            placeholder="Описание направлений деятельности..."
          />
        </Field>

        <Field>
          Контент (EN)
          <RichText
            value={form.directions_content_en}
            onChange={(value) => patch('directions_content_en', value)}
            placeholder="Business directions description..."
          />
        </Field>
      </Card>

      <Card>
        <h3>Корпоративная структура</h3>
        
        <Field>
          Заголовок (RU)
          <input
            type="text"
            value={form.structure_title_ru}
            onChange={(e) => patch('structure_title_ru', e.target.value)}
            placeholder="Корпоративная структура"
          />
        </Field>

        <Field>
          Заголовок (EN)
          <input
            type="text"
            value={form.structure_title_en}
            onChange={(e) => patch('structure_title_en', e.target.value)}
            placeholder="Corporate Structure"
          />
        </Field>

        <Field>
          Описание (RU)
          <textarea
            value={form.structure_content_ru}
            onChange={(e) => patch('structure_content_ru', e.target.value)}
            placeholder="Описание корпоративной структуры..."
            rows={3}
          />
        </Field>

        <Field>
          Описание (EN)
          <textarea
            value={form.structure_content_en}
            onChange={(e) => patch('structure_content_en', e.target.value)}
            placeholder="Corporate structure description..."
            rows={3}
          />
        </Field>

        <Field>
          Список подразделений (RU)
          <small>Каждое подразделение с новой строки</small>
          <textarea
            value={form.structure_list_ru}
            onChange={(e) => patch('structure_list_ru', e.target.value)}
            placeholder="Департамент премиальных продаж&#10;Инвестиционное подразделение&#10;Юридический департамент"
            rows={6}
          />
        </Field>

        <Field>
          Список подразделений (EN)
          <small>Каждое подразделение с новой строки</small>
          <textarea
            value={form.structure_list_en}
            onChange={(e) => patch('structure_list_en', e.target.value)}
            placeholder="Premium Sales Department&#10;Investment Division&#10;Legal Department"
            rows={6}
          />
        </Field>
      </Card>

      <Card>
        <h3>Достижения и признание</h3>
        
        <Field>
          Заголовок (RU)
          <input
            type="text"
            value={form.achievements_title_ru}
            onChange={(e) => patch('achievements_title_ru', e.target.value)}
            placeholder="Достижения и признание"
          />
        </Field>

        <Field>
          Заголовок (EN)
          <input
            type="text"
            value={form.achievements_title_en}
            onChange={(e) => patch('achievements_title_en', e.target.value)}
            placeholder="Achievements and Recognition"
          />
        </Field>

        <Field>
          Описание (RU)
          <textarea
            value={form.achievements_content_ru}
            onChange={(e) => patch('achievements_content_ru', e.target.value)}
            placeholder="Описание достижений корпорации..."
            rows={3}
          />
        </Field>

        <Field>
          Описание (EN)
          <textarea
            value={form.achievements_content_en}
            onChange={(e) => patch('achievements_content_en', e.target.value)}
            placeholder="Corporation achievements description..."
            rows={3}
          />
        </Field>

        <Field>
          Ключевые цифры (RU)
          <textarea
            value={form.achievements_highlight_ru}
            onChange={(e) => patch('achievements_highlight_ru', e.target.value)}
            placeholder="Более 1000 успешно завершенных сделок на общую сумму свыше 50 млрд рублей"
            rows={2}
          />
        </Field>

        <Field>
          Ключевые цифры (EN)
          <textarea
            value={form.achievements_highlight_en}
            onChange={(e) => patch('achievements_highlight_en', e.target.value)}
            placeholder="More than 1000 successfully completed deals worth over 50 billion rubles"
            rows={2}
          />
        </Field>

        <Field>
          Список достижений (RU)
          <small>Каждое достижение с новой строки</small>
          <textarea
            value={form.achievements_list_ru}
            onChange={(e) => patch('achievements_list_ru', e.target.value)}
            placeholder="Лауреат премии Лучшее агентство премиальной недвижимости&#10;Победитель в номинации Инновации в сфере недвижимости"
            rows={5}
          />
        </Field>

        <Field>
          Список достижений (EN)
          <small>Каждое достижение с новой строки</small>
          <textarea
            value={form.achievements_list_en}
            onChange={(e) => patch('achievements_list_en', e.target.value)}
            placeholder="Winner of Best Premium Real Estate Agency award&#10;Winner in Real Estate Innovation category"
            rows={5}
          />
        </Field>
      </Card>

      <Card>
        <h3>SEO и настройки</h3>
        
        <Field>
          Meta Title (RU)
          <input
            type="text"
            value={form.meta_title_ru}
            onChange={(e) => patch('meta_title_ru', e.target.value)}
            placeholder="Корпорация АСТОРИУС - О компании"
          />
        </Field>

        <Field>
          Meta Title (EN)
          <input
            type="text"
            value={form.meta_title_en}
            onChange={(e) => patch('meta_title_en', e.target.value)}
            placeholder="ASTORIUS Corporation - About Company"
          />
        </Field>

        <Field>
          Meta Description (RU)
          <textarea
            value={form.meta_description_ru}
            onChange={(e) => patch('meta_description_ru', e.target.value)}
            placeholder="Корпорация АСТОРИУС - ведущая компания в сфере премиальной недвижимости..."
            rows={2}
          />
        </Field>

        <Field>
          Meta Description (EN)
          <textarea
            value={form.meta_description_en}
            onChange={(e) => patch('meta_description_en', e.target.value)}
            placeholder="ASTORIUS Corporation - leading company in premium real estate..."
            rows={2}
          />
        </Field>

        <Field>
          Опубликовано
          <YesNoToggle
            value={form.is_published}
            onChange={(value) => patch('is_published', value)}
          />
        </Field>

        <Field>
          Активно
          <YesNoToggle
            value={form.is_active}
            onChange={(value) => patch('is_active', value)}
          />
        </Field>
      </Card>

      <Actions>
        <Btn $kind="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить изменения'}
        </Btn>
        <Btn onClick={loadData} disabled={loading || saving}>
          Сбросить изменения
        </Btn>
      </Actions>
    </>
  );
}